import { useCallback, useEffect, useState } from 'react'
import { interestRepository } from '@/data/interests/repositories/interest_repository_impl'
import type { Interest } from '@/domain/interests/entities/interest.entity'
import { DeleteInterestUseCase } from '@/domain/interests/usecases/delete_interest.usecase'
import { ListInterestsUseCase } from '@/domain/interests/usecases/list_interests.usecase'
import { toAppError } from '@/core/errors/app_error'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import { matchInterest } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'
import {
  initialInterestsDeleteDialogState,
  type InterestSortField,
  type InterestsDeleteDialogState,
} from '../models/interests_ui_state'

const defaultListInterests = new ListInterestsUseCase(interestRepository)
const defaultDeleteInterest = new DeleteInterestUseCase(interestRepository)

export interface UseInterestsListViewModelDeps {
  listInterests?: ListInterestsUseCase
  deleteInterest?: DeleteInterestUseCase
}

export function useInterestsListViewModel(deps: UseInterestsListViewModelDeps = {}) {
  const listInterests = deps.listInterests ?? defaultListInterests
  const deleteInterest = deps.deleteInterest ?? defaultDeleteInterest

  const [interests, setInterests] = useState<Interest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<InterestsDeleteDialogState>(
    initialInterestsDeleteDialogState
  )
  const [isDeleting, setIsDeleting] = useState(false)

  const { table, actions: tableActions } = useTableQueryState({
    items: interests,
    matchesSearch: matchInterest,
  })

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const list = await listInterests.execute({ page: 0, limit: 50 })
      setInterests(list)
    } catch (err: unknown) {
      logError('Failed to fetch interests', err)
      setError(toAppError(err, 'Failed to load interests').message)
      setInterests([])
    } finally {
      setIsLoading(false)
    }
  }, [listInterests])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openDeleteDialog = useCallback((interestId: string, interestName: string) => {
    setDeleteDialog({ isOpen: true, interestId, interestName })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(initialInterestsDeleteDialogState)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.interestId) return
    try {
      setIsDeleting(true)
      await deleteInterest.execute(deleteDialog.interestId)
      setInterests(prev => prev.filter(item => item.id !== deleteDialog.interestId))
      closeDeleteDialog()
    } catch (err: unknown) {
      logError('Failed to delete interest:', err)
      alert(getAxiosLikeErrorMessage(err, 'Failed to delete interest. Please try again.'))
    } finally {
      setIsDeleting(false)
    }
  }, [closeDeleteDialog, deleteDialog.interestId, deleteInterest])

  const handleSort = useCallback(
    (field: InterestSortField) => tableActions.handleSort(field),
    [tableActions]
  )

  return {
    state: {
      interests,
      filteredInterests: table.filteredItems,
      paginatedInterests: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    error,
    isDeleting,
    actions: {
      refresh,
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
